import React, { useState } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router';

import Button from '@/components/Button';
import FAIcon from '@/components/Icon/FAIcon';
import TeamAccountPromoDialog from '@/components/TeamAccountPromo/TeamAccountPromoDialog';
import { fetchExpertRequests } from '@/expertrequest/store';
import { RootState } from '@/store';
import { isValidSMBUtmSource, rateDisplay } from '@/utils';

import AddToExpertRequestDialog from './AddToExpertRequestDialog';

interface AddToExpertRequestButtonProps {
  profile?: any;
  profiles?: any[];
  size?: 'tiny' | 'normal' | 'large' | 'huge' | 'small' | 'medium' | 'large';
  label?: string;
  form?: string;
  className?: string;
  classes?: object;
  style?: React.CSSProperties;
  disabled?: boolean;
  fullWidth?: boolean;
  icon?: boolean;
  onClick?: () => void;
  onExpertAdd?: () => void;
}

const connector = connect(
  (rootState: RootState) => ({
    viewer: rootState.viewer,
  }),
  {
    fetchExpertRequests,
  }
);

const AddToExpertRequestButton = (
  props: AddToExpertRequestButtonProps & ConnectedProps<typeof connector>
) => {
  const [dialogOpened, setDialogOpened] = useState(false);
  const [showPromoDialog, setShowPromoDialog] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleOpen = () => {
    const { fetchExpertRequests, viewer, profile } = props;
    if (!viewer.id) {
      // Check for valid utm_source or rate display cookie
      const utmSource = searchParams.get('utm_source');
      const hasRateDisplayCookie = rateDisplay.has();
      const hasValidUtmSource = isValidSMBUtmSource(utmSource || '') && utmSource;

      // Has utm_source OR has cookie? → Navigate to request page to show Phase 2 modal
      if ((hasValidUtmSource || hasRateDisplayCookie) && profile?.url_endpoint) {
        const queryString = searchParams.toString();
        const targetUrl = `/profile/${profile.url_endpoint}/request${queryString ? `?${queryString}` : ''}`;

        // If already on request page, nothing to do — modal shows automatically
        if (window.location.pathname.includes('/request')) {
          return;
        }

        navigate(targetUrl);
        return;
      }
      // No utm_source AND no cookie - show TeamAccountPromoDialog (Contact Sales)
      setShowPromoDialog(true);
      return;
    }

    // Check if logged-in user has billing account
    const billingAccount = viewer.groups?.[0]?.billing_account;
    if (!billingAccount && profile?.url_endpoint) {
      // SMB users without billing - redirect to request page to show signup modal
      if (viewer.signup_type === 'smb') {
        // Preserve query params (UTM tracking, etc.)
        const queryString = searchParams.toString();
        navigate(`/profile/${profile.url_endpoint}/request${queryString ? `?${queryString}` : ''}`);
        return;
      }
      // Non-SMB users without billing - show Contact Sales dialog
      setShowPromoDialog(true);
      return;
    }

    fetchExpertRequests({ state: 'open' }).then(() => setDialogOpened(true));
  };

  const handleClose = () => setDialogOpened(false);

  const {
    profile,
    label = 'Add to Request',
    disabled,
    icon = true,
    onExpertAdd,
    fetchExpertRequests: _fetchExpertRequests,
    viewer: _viewer,
    ...other
  } = props;

  const profiles = profile ? [profile] : props.profiles || [];
  if (profiles.length === 0) return null;

  return (
    <React.Fragment>
      <Button
        variant="contained"
        color="lightTan"
        size="small"
        {...other}
        startIcon={icon && <FAIcon size={14} icon="plus" />}
        onClick={handleOpen}
      >
        {label}
      </Button>
      {!disabled && (
        <AddToExpertRequestDialog
          open={dialogOpened}
          profiles={profiles}
          onClose={handleClose}
          onExpertAdd={onExpertAdd}
        />
      )}
      <TeamAccountPromoDialog
        open={showPromoDialog}
        onClose={() => setShowPromoDialog(false)}
        showEventTrack="promo.show.team_account.add_to_request"
      />
    </React.Fragment>
  );
};

export default connector(AddToExpertRequestButton);
